import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import { Button, Paper, TextField, Typography, Box, InputAdornment, IconButton } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { signInAsync } from '../../redux/slices/authSlice';
import useFormInput from '../../hooks/useFormInput';
import Logo from '../../components/Logo';

const waveMove = keyframes`
  0% {
    transform: translateX(-100px) translateY(0);
  }
  50% {
    transform: translateX(100px) translateY(-50px);
  }
  100% {
    transform: translateX(-100px) translateY(0);
  }
`;

const wavePulse = keyframes`
  0%, 100% {
    opacity: 0.3;
  }
  50% {
    opacity: 0.7;
  }
`;

const BackgroundContainer = styled(Box)({
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  overflow: 'hidden',
});

const WaveLayer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'duration' && prop !== 'delay' && prop !== 'isReverse'
})<{ duration: number; delay: number; isReverse?: boolean }>(
  ({ duration, delay, isReverse }) => ({
    position: 'absolute',
    width: '120%',
    height: '120%',
    top: '-10%',
    left: '-10%',
    animation: `${waveMove} ${duration}s ease-in-out infinite, ${wavePulse} ${duration * 0.6}s ease-in-out infinite`,
    animationDelay: `${delay}s`,
    animationDirection: isReverse ? 'reverse' : 'normal',
  })
);

const FloatingPaths = ({ position }: { position: number }) => {
  const paths = useMemo(() => Array.from({ length: 36 }, (_, i) => ({
    id: i,
    d: `M-${380 - i * 5 * position} -${189 + i * 6}C-${
      380 - i * 5 * position
    } -${189 + i * 6} -${312 - i * 5 * position} ${216 - i * 6} ${
      152 - i * 5 * position
    } ${343 - i * 6}C${616 - i * 5 * position} ${470 - i * 6} ${
      684 - i * 5 * position
    } ${875 - i * 6} ${684 - i * 5 * position} ${875 - i * 6}`,
    width: 0.5 + i * 0.03,
    opacity: 0.1 + i * 0.02,
  })), [position]);

  return (
    <svg
      style={{ width: '100%', height: '100%' }}
      viewBox="0 0 696 316"
      fill="none"
    >
      {paths.map((path) => (
        <path
          key={path.id}
          d={path.d}
          stroke="#4ECDC4"
          strokeWidth={path.width}
          strokeOpacity={path.opacity}
          fill="none"
        />
      ))}
    </svg>
  );
};

const AnimatedBackground = () => {
  return (
    <BackgroundContainer>
      <WaveLayer duration={25} delay={0}>
        <FloatingPaths position={1} />
      </WaveLayer>
      <WaveLayer duration={30} delay={2} isReverse>
        <FloatingPaths position={-1} />
      </WaveLayer>
      <WaveLayer duration={20} delay={5}>
        <FloatingPaths position={0.5} />
      </WaveLayer>
    </BackgroundContainer>
  );
};

const PageContainer = styled('div')({
  width: '100vw',
  minHeight: '100vh',
  padding: '0 20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#0a0a0f',
  position: 'relative',
  overflow: 'hidden',
});

const FormContainer = styled(Paper)(({ theme }) => ({
  maxWidth: 480,
  width: '100%',
  padding: '48px 40px',
  display: 'flex',
  flexDirection: 'column',
  background: 'rgba(30, 30, 30, 0.7)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  borderRadius: 24,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  position: 'relative',
  zIndex: 1,
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 48px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.1)'
  }
}));

const LogoContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: 32,
  '& > div': {
    transform: 'scale(1.2)'
  }
});

const WelcomeText = styled(Typography)(({ theme }) => ({
  fontSize: '28px',
  fontWeight: 700,
  textAlign: 'center',
  marginBottom: 8,
  background: 'linear-gradient(135deg, #4ECDC4 0%, #3BA99F 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text'
}));

const SubText = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  textAlign: 'center',
  color: theme.palette.text.secondary,
  marginBottom: 32,
  opacity: 0.8
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: 20,
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
    fontSize: '14px'
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: theme.palette.primary.main
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    borderRadius: 12,
    transition: 'all 0.3s ease',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.1)',
      transition: 'all 0.3s ease'
    },
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      '& fieldset': {
        borderColor: 'rgba(78, 205, 196, 0.3)'
      }
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
      '& fieldset': {
        borderColor: theme.palette.primary.main,
        borderWidth: '2px'
      }
    }
  },
  '& .MuiInputBase-input': {
    padding: '14px 16px',
    fontSize: '15px'
  }
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  marginTop: 8,
  height: 52,
  fontSize: '16px',
  fontWeight: 700,
  borderRadius: 12,
  background: 'linear-gradient(135deg, #4ECDC4 0%, #3BA99F 100%)',
  color: '#FFFFFF',
  textTransform: 'none',
  boxShadow: '0 4px 16px rgba(78, 205, 196, 0.3)',
  '&:hover': {
    background: 'linear-gradient(135deg, #3BA99F 0%, #2D8C84 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(78, 205, 196, 0.4)'
  },
  '&:active': {
    transform: 'translateY(0px)'
  },
  transition: 'all 0.3s ease'
}));

const SignInPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const login = useFormInput('');
  const password = useFormInput('');
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  return (
    <PageContainer>
      <AnimatedBackground />
      <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 2 }}>
        <LanguageSwitcher />
      </Box>
      <FormContainer elevation={24}>
        <LogoContainer>
          <Logo />
        </LogoContainer>
        
        <WelcomeText>
          {t('auth.welcome')}
        </WelcomeText>
        <SubText>
          {t('auth.subtitle')}
        </SubText>

        <form
          onSubmit={event => {
            event.preventDefault();
            dispatch(
              signInAsync({ login: login.value, password: password.value })
            );
          }}
        >
          <StyledTextField
            {...login}
            fullWidth
            label={t('auth.login')}
            placeholder={t('auth.loginPlaceholder')}
            autoComplete='username'
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineIcon sx={{ color: 'rgba(78, 205, 196, 0.6)' }} />
                </InputAdornment>
              ),
            }}
          />
          
          <StyledTextField
            {...password}
            fullWidth
            label={t('auth.password')}
            placeholder={t('auth.passwordPlaceholder')}
            type={showPassword ? 'text' : 'password'}
            autoComplete='current-password'
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon sx={{ color: 'rgba(78, 205, 196, 0.6)' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                    sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          <SubmitButton
            fullWidth
            type='submit'
            variant='contained'
          >
            {t('auth.signIn')}
          </SubmitButton>
        </form>
      </FormContainer>
    </PageContainer>
  );
};

export default SignInPage;
